import { VerificationNames } from "shared/types";
import { formatToCurrency, toMoney } from "shared/utils";
import { Money } from "types/landlord/reports";
import { ApplicantOrInvitee, Verification } from "./types";
import { useCallback, useEffect, useRef, useState } from "react";
import { VerificationSchemaNames } from "types/landlord/verificationTemplate";
import { HIDDEN_VERIFICATIONS } from "./constants";
import { Applicant } from "types/application";
import { WarningsReportPayload } from "api/landlord/warnings-report/types";
import { formatPastDueAccountDisplay } from "shared/utils/past-due-account-metrics";

export const shouldShowLandlordVerification = (
  verificationSchemas?: VerificationSchemaNames
): boolean =>
  Boolean(verificationSchemas?.rental_verification) ||
  Boolean(
    verificationSchemas?.[VerificationNames.PremiumRentalHistory]
      ?.premium_rental_history_enabled
  );

export const countTriggeredRules = (
  payload?: WarningsReportPayload
): number => {
  const rules = payload?.rules;
  if (!rules) return 0;
  let count = 0;
  Object.values(rules).forEach((entry) => {
    if (entry?.triggered) count += 1;
  });
  return count;
};

export const verificationNameToTitle = (name: VerificationNames) => {
  switch (name) {
    case VerificationNames.CreditReport:
      return "Credit report (US)";
    case VerificationNames.EvictionReport:
      return "Housing court report";
    case VerificationNames.HousingHistory:
      return "Housing history";
    case VerificationNames.RentalApplication:
      return "Rental application";
    case VerificationNames.IncomeVerification:
      return "Self-reported income history";
    case VerificationNames.VerifiedIncomeVerification:
      return "Verified income history";
    case VerificationNames.CombinedIncome:
      return "Income history";
    case VerificationNames.IdentityVerification:
      return "Identity verification";
    case VerificationNames.CriminalReport:
      return "Criminal report (US)";
    case VerificationNames.CanadianCriminalReport:
      return "Criminal report (CA)";
    case VerificationNames.CanadianCreditReport:
      return "Credit report (CA)";
    case VerificationNames.PetScreeningReport:
      return "Animal screening";
    case VerificationNames.WarningsReport:
      return "Warnings report";
    case VerificationNames.BoomFinanceVerification:
      return "Purchase application";
    default:
      return "";
  }
};

export const formatCriminalSummaryItem = (
  name: string,
  count: number | undefined | null
) => {
  return `${count} ${pluralize(name, count || 0)}`;
};

export const pluralize = (name: string, count: number) => {
  // Basic pluralization: if count is not 1, add 's'
  // If we want to use it for more complicated cases, consider installing pluralize package
  return count === 1 ? name : `${name}s`;
};

export const toMoneyIfExist = (cents: number | undefined | null) => {
  if (cents === undefined || cents === null) return "-";
  return toMoney(cents / 100);
};

// Keeps the report's own currency instead of assuming USD, which matters for
// Canadian credit reports.
export const summaryMoneyToDisplay = (amount?: Money | "No data") => {
  if (!amount || amount === "No data") return null;

  return formatToCurrency(amount, { minimumFractionDigits: 2 });
};

export const sortVerifications = function sortArray<T>(
  arr: T[],
  order: string[]
): T[] {
  return arr.sort((a, b) => {
    const aValue = order.indexOf((a as Verification).name);
    const bValue = order.indexOf((b as Verification).name);

    // If aValue or bValue is -1 (not found in order array), it should be placed at the end.
    const aPos = aValue === -1 ? order.length : aValue;
    const bPos = bValue === -1 ? order.length : bValue;

    return aPos - bPos;
  });
};

/*
 * The API serialises every credit summary metric as the literal "No data" when
 * the bureau returned a no-hit, i.e. no credit file matched the applicant.
 */
export const hasNoCreditFile = (summary: Verification["summary"]) =>
  summary?.score === "No data";

export const calculatePastDueAccounts = (summary: Verification["summary"]) => {
  return formatPastDueAccountDisplay({
    metrics: summary?.past_due_account_metrics,
    isPercentage: summary?.past_due_accounts_type === "Percentage",
    noData: summary?.past_due_accounts === "No data"
  });
};

export const useApplicantsTableScroll = () => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const scrollbarRef = useRef<HTMLDivElement | null>(null);
  const tableHeaderRef = useRef<HTMLDivElement | null>(null);
  const [showMoreButton, setShowMoreButton] = useState(false);
  const isSyncing = useRef(false);
  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const firstCellRef = useRef<HTMLDivElement | null>(null);

  const syncScroll = useCallback(
    (source: "viewport" | "scrollbar" | "tableHeader") => {
      if (
        !viewportRef.current ||
        !scrollbarRef.current ||
        !tableHeaderRef.current
      )
        return;

      let target: HTMLDivElement[];

      if (source === "viewport")
        target = [scrollbarRef.current, tableHeaderRef.current];
      else if (source === "scrollbar")
        target = [viewportRef.current, tableHeaderRef.current];
      else target = [viewportRef.current, scrollbarRef.current];

      if (!isSyncing.current) {
        isSyncing.current = true;
        let scrollLeft: number;

        if (source === "viewport") scrollLeft = viewportRef.current.scrollLeft;
        else if (source === "scrollbar")
          scrollLeft = scrollbarRef.current.scrollLeft;
        else scrollLeft = tableHeaderRef.current.scrollLeft;

        target.forEach((el) => (el.scrollLeft = scrollLeft));

        requestAnimationFrame(() => {
          isSyncing.current = false;
        });
      }
    },
    []
  );

  const checkScroll = useCallback(() => {
    requestAnimationFrame(() => {
      if (viewportRef.current) {
        const { scrollWidth, clientWidth, scrollLeft } = viewportRef.current;
        setShowMoreButton(
          scrollWidth > clientWidth &&
            scrollLeft < scrollWidth - clientWidth - 1
        );
      }
    });
  }, []);

  useEffect(() => {
    checkScroll();
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    const scrollbar = scrollbarRef.current;
    const tableHeader = tableHeaderRef.current;

    if (viewport && scrollbar && tableHeader) {
      const handleViewportScroll = () => syncScroll("viewport");
      const handleScrollbarScroll = () => syncScroll("scrollbar");
      const handleTableHeaderScroll = () => syncScroll("tableHeader");

      viewport.addEventListener("scroll", handleViewportScroll);
      scrollbar.addEventListener("scroll", handleScrollbarScroll);
      tableHeader.addEventListener("scroll", handleTableHeaderScroll);

      viewport.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);

      return () => {
        viewport.removeEventListener("scroll", handleViewportScroll);
        scrollbar.removeEventListener("scroll", handleScrollbarScroll);
        tableHeader.removeEventListener("scroll", handleTableHeaderScroll);

        viewport.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [syncScroll, checkScroll]);

  const handleScrollRight = () => {
    if (viewportRef.current && scrollbarRef.current && tableHeaderRef.current) {
      const { scrollWidth, clientWidth } = viewportRef.current;
      viewportRef.current.scrollTo({
        left: scrollWidth - clientWidth,
        behavior: "smooth"
      });
      scrollbarRef.current.scrollTo({
        left: scrollWidth - clientWidth,
        behavior: "smooth"
      });
      tableHeaderRef.current.scrollTo({
        left: scrollWidth - clientWidth,
        behavior: "smooth"
      });
    }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyHeader(!entry.isIntersecting);
      },
      { root: null, threshold: 0 }
    );

    if (firstCellRef.current) observer.observe(firstCellRef.current);

    return () => {
      if (firstCellRef.current) observer.unobserve(firstCellRef.current);
    };
  }, []);

  return {
    viewportRef,
    scrollbarRef,
    tableHeaderRef,
    firstCellRef,
    showMoreButton,
    showStickyHeader,
    handleScrollRight
  };
};

export const filterVerificationByConfigValue = ({
  verificationName,
  verificationValue,
  includeHiddenVerifications
}: {
  verificationName: string;
  verificationValue: Record<string, unknown>;
  includeHiddenVerifications?: boolean;
}) =>
  !(
    (includeHiddenVerifications
      ? HIDDEN_VERIFICATIONS.includes(verificationName as VerificationNames)
      : false) ||
    (verificationName === VerificationNames.HousingHistory &&
      (
        verificationValue as {
          address_step_enabled: boolean;
        }
      ).address_step_enabled === false) ||
    (verificationName === VerificationNames.IncomeVerification &&
      (
        verificationValue as {
          income_verification_step_enabled: boolean;
        }
      ).income_verification_step_enabled === false) ||
    (verificationName === VerificationNames.PetScreeningReport &&
      !(verificationValue as { settings?: { pet_screening_enabled: boolean } })
        .settings) ||
    (verificationValue as { settings?: { pet_screening_enabled: boolean } })
      .settings?.pet_screening_enabled === false
  );

export const getAvailableVerificationNames = (
  applicantsOrInvitees: ApplicantOrInvitee[],
  applicationVerificationSchemas?: VerificationSchemaNames
) => {
  if (!applicationVerificationSchemas) return [] as VerificationNames[];
  // Verification names from application screening configs
  const applicationVerificationNames = Object.entries(
    applicationVerificationSchemas
  )
    .filter(([v, vValue]) =>
      filterVerificationByConfigValue({
        verificationName: v,
        verificationValue: vValue,
        includeHiddenVerifications: true
      })
    )
    .map((v) => v[0]) as VerificationNames[];

  const applicants = applicantsOrInvitees.filter(
    (a): a is Applicant => "verifications" in a
  );

  // Verification names extracted from existing verifications per each applicant
  const applicantsVerificationNames = Array.from(
    new Set(
      applicants.flatMap((a) =>
        (a.verifications || [])
          .map((v) => v.name)
          .filter((vName) => !HIDDEN_VERIFICATIONS.includes(vName))
      )
    )
  );

  const hasVerificationsMismatch =
    applicantsVerificationNames.length !==
      applicationVerificationNames.length ||
    applicantsVerificationNames.filter(
      (vName) => !applicationVerificationNames.includes(vName)
    ).length > 0;

  const names = hasVerificationsMismatch
    ? Array.from(
        new Set([
          ...applicantsVerificationNames,
          ...applicationVerificationNames
        ])
      )
    : applicationVerificationNames;

  return names.filter((n) => n !== VerificationNames.PremiumRentalHistory);
};
